import { useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Sparkles, Upload, Loader2, ImageIcon } from "lucide-react";
import { useAdminApi } from "./useAdminApi";
import { API, imgUrl } from "@/lib/api";

const input = "w-full bg-[#0B0F12] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#00C9B7]";
const Label = ({ children }) => <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">{children}</p>;

const parseAi = (raw) => {
  const [head, ...rest] = raw.split(/\n-{3,}\n/);
  const pick = (k) => (head.match(new RegExp(`^${k}:\\s*(.+)$`, "mi")) || [])[1]?.trim() || "";
  return { title: pick("TITLE"), meta_description: pick("META"), keywords: pick("KEYWORDS"), category: pick("CATEGORY"), excerpt: pick("EXCERPT"), content: rest.join("\n---\n").trim() };
};

const empty = { title: "", slug: "", meta_description: "", keywords: "", excerpt: "", content: "", cover_image: "", category: "Dental Care", status: "published" };

const AiBox = ({ onResult }) => {
  const api = useAdminApi();
  const [g, setG] = useState({ topic: "", keywords: "", tone: "warm and professional", language: "English", length: "medium" });
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState("");
  const set = (k) => (e) => setG({ ...g, [k]: e.target.value });

  const run = async () => {
    if (g.topic.trim().length < 3) return toast.error("Add a topic first");
    setBusy(true); setLive("");
    let raw = "";
    try {
      const res = await fetch(`${API}/admin/blogs/generate`, { method: "POST", headers: { ...(await api.headers()), "Content-Type": "application/json" }, body: JSON.stringify(g) });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const data = p.replace(/^data: /, "");
          if (data === "[DONE]") continue;
          const ev = JSON.parse(data);
          if (ev.error) throw new Error(ev.error);
          raw += ev.t; setLive(raw);
        }
      }
      onResult(parseAi(raw));
      toast.success("Draft ready – review, add an image and publish");
    } catch (e) {
      toast.error(`AI writer failed: ${e.message}`);
    }
    setBusy(false);
  };

  return (
    <div className="rounded-2xl border border-[#00C9B7]/30 bg-gradient-to-br from-[#00C9B7]/10 to-transparent p-5 sm:p-6 space-y-4" data-testid="ai-writer-box">
      <p className="font-display uppercase text-xl flex items-center gap-2"><Sparkles className="text-[#00C9B7]" size={20} /> AI blog writer</p>
      <textarea rows={2} value={g.topic} onChange={set("topic")} placeholder="Topic, e.g. Teeth whitening before your wedding in San Juan del Sur" className={input} data-testid="ai-topic-input" />
      <input value={g.keywords} onChange={set("keywords")} placeholder="Target keywords (optional), e.g. teeth whitening Nicaragua" className={input} data-testid="ai-keywords-input" />
      <div className="grid grid-cols-3 gap-3">
        <select value={g.tone} onChange={set("tone")} className={input} data-testid="ai-tone-select">{["warm and professional", "friendly and casual", "expert and clinical"].map((t) => <option key={t}>{t}</option>)}</select>
        <select value={g.language} onChange={set("language")} className={input} data-testid="ai-language-select">{["English", "Spanish"].map((t) => <option key={t}>{t}</option>)}</select>
        <select value={g.length} onChange={set("length")} className={input} data-testid="ai-length-select">{["short", "medium", "long"].map((t) => <option key={t}>{t}</option>)}</select>
      </div>
      <button onClick={run} disabled={busy} className="w-full inline-flex justify-center items-center gap-2 rounded-xl bg-[#00C9B7] text-[#031312] py-3.5 text-xs font-bold uppercase tracking-widest hover:bg-[#00E0CC] disabled:opacity-60" data-testid="ai-generate-btn">{busy ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} {busy ? "Writing…" : "Generate SEO blog"}</button>
      {busy && <pre className="max-h-48 overflow-y-auto whitespace-pre-wrap text-xs text-slate-400 bg-black/30 rounded-xl p-3" data-testid="ai-live-output">{live || "Thinking…"}</pre>}
    </div>
  );
};

export default function BlogEditor({ post, onDone }) {
  const api = useAdminApi();
  const [f, setF] = useState(post ? { ...post, keywords: (post.keywords || []).join(", ") } : empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("write");
  const fileRef = useRef();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { headers: await api.headers() });
      setF((x) => ({ ...x, cover_image: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const save = async (status) => {
    setSaving(true);
    const body = { ...f, status, keywords: f.keywords.split(",").map((k) => k.trim()).filter(Boolean) };
    try {
      if (post) await api.put(`/admin/blogs/${post.id}`, body);
      else await api.post("/admin/blogs", body);
      toast.success(status === "published" ? "Published!" : "Saved as draft");
      onDone();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Please add a title and content");
    }
    setSaving(false);
  };

  return (
    <div data-testid="blog-editor">
      <button onClick={onDone} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-white mb-6" data-testid="blog-editor-back-btn"><ArrowLeft size={14} /> Back to posts</button>
      <div className="grid xl:grid-cols-5 gap-6">
        <div className="xl:col-span-2 space-y-5">
          <AiBox onResult={(r) => setF((x) => ({ ...x, ...r, category: r.category || x.category }))} />
          <div className="rounded-2xl bg-[#131B21] border border-white/5 p-5 space-y-3">
            <Label>Cover image</Label>
            <div onClick={() => fileRef.current.click()} className="cursor-pointer aspect-[16/9] rounded-xl border border-dashed border-white/15 overflow-hidden flex items-center justify-center hover:border-[#00C9B7]" data-testid="blog-image-dropzone">
              {f.cover_image ? <img src={imgUrl(f.cover_image)} alt="cover" className="w-full h-full object-cover" /> : <div className="text-center text-slate-500 text-sm"><ImageIcon className="mx-auto mb-2" />Click to upload</div>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} data-testid="blog-image-input" />
            <button onClick={() => fileRef.current.click()} disabled={uploading} className="w-full inline-flex justify-center items-center gap-2 rounded-xl border border-white/15 py-2.5 text-xs uppercase tracking-widest hover:border-[#00C9B7]" data-testid="blog-image-upload-btn">{uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Upload image</button>
          </div>
          <div className="rounded-2xl bg-[#131B21] border border-white/5 p-5 space-y-4">
            <div><Label>SEO meta description ({f.meta_description.length}/158)</Label><textarea rows={3} value={f.meta_description} onChange={set("meta_description")} className={input} data-testid="blog-meta-input" /></div>
            <div><Label>Keywords (comma separated)</Label><input value={f.keywords} onChange={set("keywords")} className={input} data-testid="blog-keywords-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label><input value={f.category} onChange={set("category")} className={input} data-testid="blog-category-input" /></div>
              <div><Label>URL slug</Label><input value={f.slug} onChange={set("slug")} placeholder="auto from title" className={input} data-testid="blog-slug-input" /></div>
            </div>
          </div>
        </div>
        <div className="xl:col-span-3 rounded-2xl bg-[#131B21] border border-white/5 p-5 space-y-4">
          <div><Label>Title</Label><input value={f.title} onChange={set("title")} className={`${input} text-lg font-semibold`} data-testid="blog-title-input" /></div>
          <div><Label>Excerpt</Label><textarea rows={2} value={f.excerpt} onChange={set("excerpt")} className={input} data-testid="blog-excerpt-input" /></div>
          <div className="flex gap-1 bg-[#0B0F12] rounded-xl p-1 w-fit">
            {["write", "preview"].map((t) => <button key={t} onClick={() => setTab(t)} className={`px-4 py-1.5 rounded-lg text-xs uppercase tracking-widest ${tab === t ? "bg-[#00C9B7] text-[#031312] font-bold" : "text-slate-400"}`} data-testid={`blog-editor-tab-${t}`}>{t}</button>)}
          </div>
          {tab === "write" ? <textarea rows={22} value={f.content} onChange={set("content")} placeholder="Article in Markdown (## headings, - lists)…" className={`${input} font-mono text-xs leading-relaxed`} data-testid="blog-content-input" /> : <div className="prose-cora max-h-[560px] overflow-y-auto bg-[#0B0F12] rounded-xl p-5" data-testid="blog-preview"><ReactMarkdown>{f.content}</ReactMarkdown></div>}
          <div className="flex flex-wrap gap-3 justify-end">
            <button onClick={() => save("draft")} disabled={saving} className="rounded-full border border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white/10" data-testid="blog-save-draft-btn">Save draft</button>
            <button onClick={() => save("published")} disabled={saving} className="rounded-full bg-[#00C9B7] text-[#031312] px-7 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#00E0CC] inline-flex items-center gap-2" data-testid="blog-publish-btn">{saving && <Loader2 className="animate-spin" size={14} />} Publish</button>
          </div>
        </div>
      </div>
    </div>
  );
}
